/**
 * Drug Search Page
 * Main search interface for Drug Products
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import useDrugSearch from './hooks/useDrugSearch';
import DrugSearchForm from './components/DrugSearchForm';
import DrugResultsTable from './components/DrugResultsTable';
import useAPICall from '../../../hooks/useAPICall';
import { API_CONFIG } from '../../../config/api.config';
import apiClient from '../../../services/apiClient';
import { buildDrugQuery, normalizeSolrArray } from '../../../utils/solrQuery';

const DrugSearch = () => {
  const [exportLoading, setExportLoading] = React.useState(false);
  const { loading: savingEdits, execute: executeSave } = useAPICall();
  const { loading, results, totalCount, filters, facets, page, pageSize, autoSearch, search, updateFilters, clearFilters, setPage, setPageSize, setAutoSearch } = useDrugSearch();

  const getCurrentUserName = () => {
    try {
      const rawUser = sessionStorage.getItem('user') || localStorage.getItem('user');
      if (!rawUser) return '';
      const user = JSON.parse(rawUser);
      return user?.name || user?.username || '';
    } catch {
      return '';
    }
  };

  const normalizeSingleValue = (value) => {
    if (Array.isArray(value)) return value[0] ?? '';
    return value ?? '';
  };

  const downloadCsv = (rows, columns, fileName) => {
    const header = columns.join(',');
    const csvRows = rows.map((row) => columns
      .map((key) => {
        const value = row?.[key] ?? '';
        const safe = String(value).replaceAll('"', '""');
        return `"${safe}"`;
      })
      .join(',')
    );

    const csvData = [header, ...csvRows].join('\n');
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const rowsToFetch = Math.max(totalCount, 1);
      const q = buildDrugQuery(filters, 1, rowsToFetch);
      const response = await apiClient.post(API_CONFIG.drug.search.endpoint, { q });
      const docs = response?.data?.response?.docs || [];
      const exportRows = docs.map((doc) => ({
        id: normalizeSingleValue(doc.drug_id) || normalizeSingleValue(doc.brand_id) || doc.id,
        drugId: normalizeSingleValue(doc.drug_id),
        brandId: normalizeSingleValue(doc.brand_id),
        ndc: normalizeSolrArray(doc.ndc),
        drugName: normalizeSolrArray(doc.drug_name),
        brandName: normalizeSolrArray(doc.brand_name),
        formTlc3: normalizeSolrArray(doc.form_tlc3 || doc.formtlc),
        clientDrugs: normalizeSolrArray(doc.client_drug_flag),
        clientDrugFlag:
          doc.client_drug_flag === true
            || String(doc.client_drug_flag).toLowerCase() === 'true',
        manufacturerName: normalizeSolrArray(doc.manufacturer_name),
        manufacturer: normalizeSolrArray(doc.manufacturer_name),
        productSum: normalizeSolrArray(doc.npa_product_sum),
        brandType: normalizeSolrArray(doc.brand_type),
        clientBrandType: normalizeSolrArray(doc.client_brand_type),
        pgnKey: normalizeSolrArray(doc.xpt_pgn_key),
        pgnDescription: normalizeSolrArray(doc.xpt_pgn_desc),
      }));

      const columns = [
        'ndc', 'drugName', 'brandName', 'formTlc3', 'clientDrugFlag', 'manufacturerName',
        'productSum', 'clientBrandType', 'pgnKey', 'pgnDescription'
      ];

      downloadCsv(exportRows, columns, 'drug-search-results.csv');
    } catch {
      toast.error('Unable to export search results. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSaveEdits = async (changes) => {
    if (!Array.isArray(changes) || changes.length === 0) {
      toast.info('No row changes to save.');
      return;
    }

    const updatedBy = getCurrentUserName();
    const toNumberOrUndefined = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const toNdcList = (value) => {
      if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
      if (typeof value === 'string') {
        return value.split(',').map((item) => item.trim()).filter(Boolean);
      }
      return [];
    };

    const rowChanges = changes
      .filter((item) => item.regularChanged)
      .map((item) => ({
        drugId: toNumberOrUndefined(item.drugId) ?? item.drugId,
        drugName: item.drugName,
        brandId: toNumberOrUndefined(item.brandId),
        brandName: item.brandName,
        clientDrugFlag: item.clientDrugFlag,
      }))
      .filter((item) => item.brandId !== undefined);

    const brandTypeChanges = Array.from(
      changes
        .filter((item) => item.brandTypeChanged)
        .reduce((acc, item) => {
          const brandId = toNumberOrUndefined(item.brandId);
          const clientBrandType = String(item.clientBrandType || '').trim();
          if (brandId === undefined || !clientBrandType) {
            return acc;
          }

          const key = `${brandId}::${clientBrandType}`;
          const existing = acc.get(key) || {
            brandId,
            clientBrandType,
            ndc: [],
            updatedBy,
          };

          toNdcList(item.ndc).forEach((ndc) => {
            if (!existing.ndc.includes(ndc)) {
              existing.ndc.push(ndc);
            }
          });

          acc.set(key, existing);
          return acc;
        }, new Map())
        .values()
    );

    const requestResults = await Promise.allSettled([
      rowChanges.length > 0
        ? executeSave(
          API_CONFIG.drug.submitChanges,
          rowChanges,
          {
            headers: { updatedBy },
            showSuccessToast: false,
            showErrorToast: false,
            errorMessage: 'Unable to update drug records.',
          }
        )
        : Promise.resolve(),
      ...brandTypeChanges.map((payload) =>
        apiClient.put(API_CONFIG.drug.submitTypeChanges.endpoint, payload)
      ),
    ]);

    const failures = requestResults.filter((result) => result.status === 'rejected');
    const successes = requestResults.length - failures.length;

    if (failures.length === 0) {
      toast.success('Drug updates saved successfully.');
      await search();
      return;
    }

    if (successes > 0) {
      toast.warning('Some drug updates failed.');
      await search();
      return;
    }

    toast.error('Unable to update drug records.');
  };

  return (
    <Box sx={{ width: '100%', pb: 1 }}>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1,
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
      <Box sx={{ mb: 1, px: 0.5 }}>
        <Typography variant='h6' sx={{ fontWeight: 600,  color: '#0f172a', mb: 0.25 }}>Drug Search</Typography>
        <Typography variant="body2" sx={{mb:1.3}} color="text.secondary">
          Search and manage drug product records and NDCs
        </Typography>
      </Box>
        <DrugSearchForm
          filters={filters}
          facets={facets}
          onUpdateFilters={updateFilters}
          onSearch={search}
          onClear={clearFilters}
          autoSearch={autoSearch}
          onAutoSearchChange={setAutoSearch}
          loading={loading}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}
      >
        <DrugResultsTable
          results={results}
          brandOptions={facets?.brandNames || []}
          brandTypeOptions={facets?.brandTypes || []}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onExport={handleExport}
          exportLoading={exportLoading}
          onSaveEdits={handleSaveEdits}
          savingEdits={savingEdits}
          loading={loading}
        />
      </Paper>
    </Box>
  );
};

export default DrugSearch;
